import React, { useRef, useEffect } from "react";
import { useMessages } from "./MessageContext.jsx";
import "./Banner.css";

const MessageItem = ({ message, isLatest = false }) => {
  const { removeMessage } = useMessages();

  const getIcon = (type) => {
    switch (type) {
      case "success":
        return "✅";
      case "error":
        return "❌";
      case "warning":
        return "⚠️";
      case "info":
      default:
        return "ℹ️";
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const handleClose = () => {
    removeMessage(message.id);
  };

  return (
    <div className={`message-item message-item--${message.type} ${isLatest ? 'message-item--latest' : ''}`}>
      <span className="message-item__icon" aria-hidden="true">
        {getIcon(message.type)}
      </span>
      <span className="message-item__text">{message.message}</span>
      {!isLatest && (
        <span className="message-item__time">{formatTime(message.timestamp)}</span>
      )}
      <button
        className="message-item__close"
        onClick={handleClose}
        aria-label="Remove message"
      >
        ×
      </button>
    </div>
  );
};

const MessageBanner = () => {
  const { messages, latestMessage, isExpanded, toggleExpanded, clearMessages } = useMessages();
  const historyRef = useRef(null);

  // Auto scroll to bottom of history when expanded
  useEffect(() => {
    if (isExpanded && historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [isExpanded, messages.length]);

  // Don't render if no messages
  if (messages.length === 0) return null;

  const historyMessages = messages.slice(0, -1); // All messages except the latest

  return (
    <div className="message-banner-container">
      {/* Main banner with latest message */}
      {latestMessage && (
        <div className="message-banner">
          <div className="message-banner__content">
            <MessageItem message={latestMessage} isLatest={true} />
          </div>
          <div className="message-banner__controls">
            {historyMessages.length > 0 && (
              <button 
                className="message-banner__history-toggle"
                onClick={toggleExpanded}
                aria-label={isExpanded ? "Hide message history" : "Show message history"}
                title={`${historyMessages.length} previous message${historyMessages.length === 1 ? '' : 's'}`}
              >
                {historyMessages.length} ⇅
              </button>
            )}
            <button
              className="message-banner__clear"
              onClick={clearMessages}
              aria-label="Clear all messages"
              title="Clear all messages"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Expandable history */}
      {isExpanded && historyMessages.length > 0 && (
        <div className="message-history" ref={historyRef}>
          <div className="message-history__header">
            <span>Message History</span>
            <button 
              className="message-history__close"
              onClick={toggleExpanded}
              aria-label="Hide message history"
            >
              ×
            </button>
          </div>
          <div className="message-history__content">
            {historyMessages.map((message) => (
              <MessageItem key={message.id} message={message} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBanner;
